import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { CustomerFile, CustomerFileKind } from '@/types/domain';

const BUCKET = 'brix-order-files';
const QUERY_KEY = ['customer-files'] as const;

interface CustomerFileRow {
  id: string;
  customer_id: string;
  uploaded_by: string | null;
  order_id: string | null;
  bucket: string;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  kind: CustomerFileKind;
  notes: string | null;
  is_customer_visible: boolean;
  created_at: string;
  updated_at: string;
}

function rowToCustomerFile(row: CustomerFileRow): CustomerFile {
  return {
    id: row.id,
    customerId: row.customer_id,
    uploadedBy: row.uploaded_by,
    orderId: row.order_id,
    bucket: row.bucket,
    storagePath: row.storage_path,
    fileName: row.file_name,
    mimeType: row.mime_type,
    sizeBytes: row.size_bytes,
    kind: row.kind,
    notes: row.notes,
    isCustomerVisible: row.is_customer_visible,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Strip everything but [A-Za-z0-9._-] from a filename so the storage path
 * stays safe. Length-cap so we don't blow past Storage path limits.
 */
function safeName(name: string): string {
  const cleaned = name.replace(/[^\w.\-]+/g, '_').replace(/_+/g, '_');
  return cleaned.slice(0, 120) || 'file';
}

/**
 * useCustomerFiles — list the current customer's files.
 *
 * RLS already restricts to the current customer. We additionally filter
 * is_customer_visible = true so internal-only admin uploads don't appear
 * in the customer-facing library.
 */
export function useCustomerFiles() {
  return useQuery<CustomerFile[]>({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<CustomerFile[]> => {
      const { data, error } = await supabase
        .schema('orders')
        .from('customer_files')
        .select(
          'id, customer_id, uploaded_by, order_id, bucket, storage_path, file_name, mime_type, size_bytes, kind, notes, is_customer_visible, created_at, updated_at',
        )
        .eq('is_customer_visible', true)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as CustomerFileRow[] | null)?.map(rowToCustomerFile) ?? [];
    },
    staleTime: 30 * 1000,
  });
}

interface UploadInput {
  customerId: string;
  file: File;
  kind: CustomerFileKind;
  notes?: string;
}

/**
 * useUploadCustomerFile — upload a file to the brix-order-files bucket and
 * insert a row into orders.customer_files. The bucket's Storage RLS policy
 * requires the path to begin with the customer's UUID, so we always prefix
 * with `${customerId}/`.
 */
export function useUploadCustomerFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, file, kind, notes }: UploadInput) => {
      // `${customerId}/${YYYYMMDDHHmmss}-${safeName}` keeps paths globally
      // unique even if a customer uploads two files with the same name.
      const stamp = new Date().toISOString().replace(/[:.TZ-]/g, '').slice(0, 14);
      const path = `${customerId}/${stamp}-${safeName(file.name)}`;

      const upload = await supabase.storage.from(BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || undefined,
      });
      if (upload.error) throw upload.error;

      const { data: authData } = await supabase.auth.getUser();
      const uploadedBy = authData.user?.id ?? null;

      const insertRes = await supabase
        .schema('orders')
        .from('customer_files')
        .insert({
          customer_id: customerId,
          uploaded_by: uploadedBy,
          bucket: BUCKET,
          storage_path: path,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
          kind,
          notes: notes?.trim() ? notes.trim() : null,
          is_customer_visible: true,
        })
        .select('id')
        .single();

      if (insertRes.error) {
        // Best-effort cleanup so we don't leave orphaned objects in Storage.
        await supabase.storage.from(BUCKET).remove([path]);
        throw insertRes.error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Request a short-lived signed URL for a file in the bucket. We don't use
 * public URLs — the bucket is private. 5 minutes is enough for the browser
 * to start the download.
 */
export async function getSignedDownloadUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, 60 * 5);
  if (error) throw error;
  return data.signedUrl;
}
